import type ITimeKeeper from 'Models/ITimeKeeper';
import { countInBeatNow } from 'Utils/playAlongCountIn';

/** A cursor the count-in can hold on its note and pulse. */
export interface CountInHoldableCursor {
  pause(): void;
  unpause(): void;
  blink(durationMs: number): void;
}

/**
 * A beat already this far under way when the hold first looks is not pulsed
 * (it is the beat before the first click); a fresher one is (the continuous
 * loop can start its hold just after its first click).
 */
const FRESH_BEAT_SECS = 0.1;

/**
 * Holds the cursor on the first note while the transport counts in (ungraded
 * play), pulses it on each count-in beat, and lets it run the moment the
 * transport reaches its start. The scene calls `update` every frame.
 *
 * `onBeat` is the hook for the count-in visuals (the ring and the countdown
 * number): it runs on each count-in beat with that beat's grid index.
 */
export default class CountInCursorHold {
  private cursor: CountInHoldableCursor | null = null;
  private lastBeatIndex: number | null = null;

  constructor(private readonly onBeat: (beatIndex: number) => void = () => undefined) {}

  /** Stop `cursor` on its note until the count-in ends. */
  hold(cursor: CountInHoldableCursor): void {
    cursor.pause();
    this.cursor = cursor;
    this.lastBeatIndex = null;
  }

  /** Drop the hold without letting the cursor run (playback paused). */
  cancel(): void {
    this.cursor = null;
    this.lastBeatIndex = null;
  }

  isHolding(): boolean {
    return this.cursor !== null;
  }

  update(timeKeeper: ITimeKeeper, pickupMeasures: number): void {
    const cursor = this.cursor;
    if (cursor === null) return;
    if (!timeKeeper.isPaused() && !timeKeeper.getIsCountingIn()) {
      this.cancel();
      cursor.unpause();
      return;
    }
    const beat = countInBeatNow(timeKeeper, pickupMeasures);
    if (beat === null || beat.beatIndex === this.lastBeatIndex) return;
    const isFirstLook = this.lastBeatIndex === null;
    this.lastBeatIndex = beat.beatIndex;
    if (isFirstLook && beat.startedSecsAgo > FRESH_BEAT_SECS) return;
    cursor.blink(60000 / timeKeeper.getBpm());
    this.onBeat(beat.beatIndex);
  }
}
